import React, { useState } from 'react';
import { ElectricityPrice } from '../types';
import { parseISO, format, isWithinInterval } from 'date-fns';
import { nb } from 'date-fns/locale';
import { Clock, Check, AlertCircle, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';

interface BestTimeToUseProps {
  prices: ElectricityPrice[];
}

const BestTimeToUse: React.FC<BestTimeToUseProps> = ({ prices }) => {
  const [showTips, setShowTips] = useState(false);
  
  if (prices.length === 0) return null;

  const now = new Date();

  // Sort prices from lowest to highest
  const sortedPrices = [...prices].sort((a, b) => a.NOK_per_kWh - b.NOK_per_kWh);

  // Get 25% cheapest and most expensive hours
  const cheapestHours = sortedPrices.slice(0, Math.max(1, Math.floor(prices.length * 0.25)));
  const expensiveHours = sortedPrices.slice(-Math.max(1, Math.floor(prices.length * 0.25)));

  // Find the best 3-hour period for electricity usage
  let bestBlock = { startIndex: 0, endIndex: 2, avgPrice: Number.MAX_VALUE };

  for (let i = 0; i <= prices.length - 3; i++) {
    const blockPrices = prices.slice(i, i + 3);
    const avgPrice = blockPrices.reduce((sum, p) => sum + p.NOK_per_kWh, 0) / blockPrices.length;

    if (avgPrice < bestBlock.avgPrice) {
      bestBlock = { startIndex: i, endIndex: i + 2, avgPrice };
    }
  }

  // Extract the actual time periods
  const bestStartTime = parseISO(prices[bestBlock.startIndex].time_start);
  const bestEndTime = parseISO(prices[bestBlock.endIndex].time_end);

  return (
    <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-dark-border">
      {/* Header */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 flex items-center">
        <div className="bg-blue-200 dark:bg-blue-800 p-2 rounded-md mr-3">
          <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Beste tidspunkt å bruke strøm</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Få mest mulig ut av strømmen din ved å bruke den når den er billigst.
          </p>
        </div>
      </div>

      {/* Best 3-hour period */}
      <div 
        className="p-4"
        style={{
          containIntrinsicSize: '0 120px',
          contentVisibility: 'auto'
        }}
      >
        <h3 className="font-medium text-gray-800 dark:text-gray-200 flex items-center mb-2">
          <Check className="h-4 w-4 text-green-500 dark:text-green-400 mr-1" />
          Beste 3-timers periode
        </h3>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
            <div>
              <p className="text-base text-green-800 dark:text-green-200 font-medium">
                {format(bestStartTime, 'HH:mm', { locale: nb })} - {format(bestEndTime, 'HH:mm', { locale: nb })}
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                Gjennomsnittspris: {bestBlock.avgPrice.toFixed(2)} kr/kWh
              </p>
            </div>
            <div className="mt-2 sm:mt-0 text-sm bg-green-100 dark:bg-green-800/60 rounded-full px-3 py-1 text-green-900 dark:text-green-100">
              Ideelt for vask og lading
            </div>
          </div>
        </div>
      </div>

      {/* Cheapest and most expensive hours - mobile optimized layout */}
      <div 
        className="px-4 pb-4 space-y-3"
        style={{
          containIntrinsicSize: '0 240px',
          contentVisibility: 'auto'
        }}
      >
        {/* Cheapest Hours */}
        <div>
          <h4 className="font-medium text-gray-800 dark:text-gray-100 flex items-center mb-2">
            <Check className="h-4 w-4 text-green-500 dark:text-green-400 mr-1" />
            Billigste timer
          </h4>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-2">
              {cheapestHours.map((price) => {
                const startTime = parseISO(price.time_start);
                const isActive = isWithinInterval(now, { start: startTime, end: parseISO(price.time_end) });

                return (
                  <div
                    key={price.time_start}
                    className={`px-3 py-1 rounded text-sm font-medium text-center transition-all ${
                      isActive 
                        ? 'bg-green-500 text-white dark:bg-green-600 dark:text-white shadow-md' 
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 dark:hover:bg-green-900/50'
                    }`}
                  >
                    {format(startTime, 'HH:mm', { locale: nb })} - {price.NOK_per_kWh.toFixed(2)} kr
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Most Expensive Hours */}
        <div>
          <h4 className="font-medium text-gray-800 dark:text-gray-100 flex items-center mb-2">
            <AlertCircle className="h-4 w-4 text-red-500 dark:text-red-400 mr-1" />
            Dyreste timer
          </h4>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-2">
              {expensiveHours.map((price) => {
                const startTime = parseISO(price.time_start);
                const isActive = isWithinInterval(now, { start: startTime, end: parseISO(price.time_end) });

                return (
                  <div
                    key={price.time_start}
                    className={`px-3 py-1 rounded text-sm font-medium text-center transition-all ${
                      isActive 
                        ? 'bg-red-500 text-white dark:bg-red-600 dark:text-white shadow-md' 
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200 dark:hover:bg-red-900/50'
                    }`}
                  >
                    {format(startTime, 'HH:mm', { locale: nb })} - {price.NOK_per_kWh.toFixed(2)} kr
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible Energy-saving tips - touch optimized */}
      <div className="px-4 pb-4">
        <button 
          onClick={() => setShowTips(!showTips)}
          className="w-full flex items-center justify-between py-3 px-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 text-left"
        >
          <div className="flex items-center">
            <Lightbulb className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" />
            <span className="font-medium text-gray-900 dark:text-white">Strømsparetips</span>
          </div>
          {showTips ? (
            <ChevronUp className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          )}
        </button>
        
        {showTips && (
          <div className="mt-2 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <ul className="text-sm text-yellow-700 dark:text-yellow-200 space-y-2 list-disc pl-5">
              <li>Bruk vaskemaskin, tørketrommel og oppvaskmaskin i de billigste timene.</li>
              <li>Lad elbilen når strømmen er billigst.</li>
              <li>Senk temperaturen på ovner og varmekabler i de dyreste timene.</li>
              <li>Unngå å bruke flere strømkrevende apparater samtidig i de dyreste timene.</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default BestTimeToUse